import { createReducer, on } from '@ngrx/store';
import { IGuardian } from 'src/app/core/models/user';
import { resetAppState } from '../global/global.actions';
import { GuardianActions } from './guardian.actions';

export const itemsFeatureKey = 'guardian';

export interface GuardianState {
  selectedGuardianId: string | null;
  loggedInGuardianId: string | null;
  guardians: IGuardian[];
  loading: boolean;
  loadingInitialGuardian: boolean;
  error: unknown | null;
}

export const initialGuardianState: GuardianState = {
  selectedGuardianId: null,
  loggedInGuardianId: null,
  guardians: [],
  loading: false,
  loadingInitialGuardian: false,
  error: null,
};

export const guardianReducer = createReducer(
  initialGuardianState,
  on(GuardianActions.loadLoggedinGuardian, (state) => ({
    ...state,
    loading: true,
    loadingInitialGuardian: true,
    error: null,
  })),
  on(GuardianActions.loadLoggedinGuardianSuccess, (state, { data }) => ({
    ...state,
    guardians: [...state.guardians.filter((g) => g.id !== data.id), data],
    loggedInGuardianId: data.id,
    loading: false,
    loadingInitialGuardian: false,
    error: null,
  })),
  on(GuardianActions.loadGuardians, (state) => ({
    ...state,
    loading: true,
    error: null,
  })),
  on(GuardianActions.loadGuardiansSuccess, (state, { data }) => {
    const newIds = new Set(data.map((g) => g.id));
    return {
      ...state,
      guardians: [...state.guardians.filter((g) => !newIds.has(g.id)), ...data],
      loading: false,
      error: null,
    };
  }),
  on(GuardianActions.loadGuardiansFailure, (state, { error }) => ({
    ...state,
    loading: false,
    error,
  })),
  on(GuardianActions.addGuardian, (state, { guardian }) => ({
    ...state,
    guardians: [...state.guardians.filter((g) => g.id !== guardian.id), guardian],
    loading: false,
    error: null,
  })),
  on(GuardianActions.updateGuardian, (state) => ({
    ...state,
    loading: true,
    error: null,
  })),
  on(GuardianActions.updateLearnerAccess, (state) => ({
    ...state,
    loading: true,
    error: null,
  })),
  on(GuardianActions.updateLearnerAccessSuccess, (state, { guardian }) => ({
    ...state,
    guardians: state.guardians.map((g) => {
      if (g.id === guardian.id) {
        // Filter out null userType entries when updating state
        const filteredLearnerAccess = guardian.learnerAccess.filter((access) => access.userType !== null);
        return {
          ...guardian,
          learnerAccess: filteredLearnerAccess,
        };
      }
      return g;
    }),
    loading: false,
    error: null,
  })),
  on(GuardianActions.updateLearnerAccessFailure, (state, { error }) => ({
    ...state,
    loading: false,
    error,
  })),
  on(GuardianActions.updateGuardianSuccess, (state, { guardian }) => ({
    ...state,
    guardians: state.guardians.map((g) => (g.id === guardian.id ? guardian : g)),
    loading: false,
    error: null,
  })),
  on(GuardianActions.updateGuardianFailure, (state, { error }) => ({
    ...state,
    loading: false,
    error,
  })),
  on(GuardianActions.deleteGuardian, (state, { id }) => ({
    ...state,
    guardians: state.guardians.filter((g) => g.id !== id),
    loading: false,
    error: null,
  })),
  on(GuardianActions.selectGuardian, (state, { id }) => ({
    ...state,
    selectedGuardianId: id,
  })),
  on(GuardianActions.setLoggedinGuardian, (state, { id }) => ({
    ...state,
    loggedInGuardianId: id,
  })),
  on(GuardianActions.sendGuardianInvite, (state) => ({
    ...state,
    loading: true,
    error: null,
  })),
  on(GuardianActions.sendGuardianInviteSuccess, (state, { guardian }) => ({
    ...state,
    guardians: state.guardians.map((g) => (g.id === guardian.id ? guardian : g)),
    loading: false,
    error: null,
  })),
  on(GuardianActions.sendGuardianInviteFailure, (state, { error }) => ({
    ...state,
    loading: false,
    error,
  })),
  on(resetAppState, () => initialGuardianState),
);
